import React from 'react';
import {
  Calendar, Truck, Clock, CheckCircle, AlertCircle, X, DollarSign
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { resolveLabelSrc, openLabelInNewTab, printLabel, downloadLabel } from '../../utils/labelUtils';

const CANCELLATION_REASONS = [
  'Customer not available',
  'Package too large',
  'Incorrect address',
  'Missing label',
  'Weather delay',
  'Vehicle issue',
  'Safety concern',
  'other',
];

const AdminBookingsTab = ({
  bookings,
  statusUpdate,
  setStatusUpdate,
  cancellationReason,
  setCancellationReason,
  customReason,
  setCustomReason,
  updatePackageStatus,
  refundBooking,
}) => {
  return (
    <div data-testid="admin-bookings-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>All Bookings</h2>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Total: {bookings?.length || 0} bookings
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {bookings && bookings.map(booking => (
          <Card key={booking.id} style={{ border: '1px solid #e5e7eb' }}>
            <CardHeader style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <CardTitle style={{ fontSize: '1.1rem' }}>
                    #{booking.confirmation_number}
                  </CardTitle>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    {booking.customer_name} - {booking.customer_email}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981' }}>
                    ${booking.total_cost?.toFixed(2)}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    {booking.pickup_date}
                  </p>
                  {booking.payment_status === 'refunded' ? (
                    <span
                      data-testid={`refunded-badge-${booking.id}`}
                      style={{
                        display: 'inline-block',
                        marginTop: '0.5rem',
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                      }}
                    >
                      💸 REFUNDED ${booking.refund_amount ? Number(booking.refund_amount).toFixed(2) : ''}
                    </span>
                  ) : (
                    booking.stripe_session_id && (
                      <Button
                        size="sm"
                        onClick={() => refundBooking && refundBooking(booking)}
                        data-testid={`refund-btn-${booking.id}`}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.4rem 0.75rem',
                          fontSize: '0.75rem',
                          backgroundColor: '#7c2d12',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      >
                        <DollarSign size={12} style={{ marginRight: '4px' }} />
                        Refund
                      </Button>
                    )
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent style={{ padding: '1rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', fontSize: '0.875rem' }}>
                  <div>
                    <span style={{ color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Location:</span>
                    <p style={{ fontWeight: '600', marginTop: '0.25rem', wordBreak: 'break-word' }}>{booking.location_name}</p>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Pickup Window:</span>
                    <p style={{ fontWeight: '600', marginTop: '0.25rem' }}>{booking.pickup_window}</p>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Packages:</span>
                    <p style={{ fontWeight: '600', marginTop: '0.25rem' }}>{booking.number_of_packages} package(s)</p>
                  </div>
                </div>

                {booking.hasOversizedPackage && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fef3c7',
                    border: '2px solid #f59e0b',
                    borderRadius: '8px',
                  }}>
                    <p style={{
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      color: '#92400e',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}>
                      📏 <span>OVERSIZED PACKAGE - Requires Extra Space</span>
                    </p>
                    <p style={{
                      fontSize: '0.75rem',
                      color: '#78350f',
                      margin: '0.25rem 0 0 1.75rem',
                    }}>
                      Max size: 36×24×24 inches | Must be pre-boxed by customer
                    </p>
                  </div>
                )}
              </div>

              {/* Package Status Management */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem' }}>Package Management:</h4>
                {booking.packages && booking.packages.map((pkg, idx) => (
                  <div key={`${booking.id}-pkg-${pkg.package_number || idx}`} style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <p style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '0.25rem' }}>Package {idx + 1}</p>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          Carrier: {pkg.carrier} | Type: {pkg.package_type}
                        </p>
                        {pkg.tracking_last6 && (
                          <p style={{ fontSize: '0.875rem', color: '#10b981', fontWeight: '600', marginTop: '0.25rem' }}>
                            Tracking: ...{pkg.tracking_last6}
                          </p>
                        )}
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {pkg.packaging_size && (
                            <span style={{
                              fontSize: '0.75rem',
                              color: '#7c3aed',
                              fontWeight: '700',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#f3e8ff',
                              borderRadius: '4px',
                              border: '1px solid #c084fc',
                            }}>
                              📦 {pkg.packaging_size.charAt(0).toUpperCase() + pkg.packaging_size.slice(1)} Box
                            </span>
                          )}
                          {pkg.rush_delivery && (
                            <span style={{
                              fontSize: '0.75rem',
                              color: '#dc2626',
                              fontWeight: '700',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#fee2e2',
                              borderRadius: '4px',
                              border: '1px solid #f87171',
                            }}>
                              ⚡ RUSH Delivery
                            </span>
                          )}
                          {pkg.isFragile && (
                            <span style={{
                              fontSize: '0.75rem',
                              color: '#ea580c',
                              fontWeight: '700',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#ffedd5',
                              borderRadius: '4px',
                              border: '1px solid #fb923c',
                            }}>
                              ⚠️ FRAGILE
                            </span>
                          )}
                        </div>
                      </div>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: pkg.status === 'delivered' ? '#d1fae5' : pkg.status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                        color: pkg.status === 'delivered' ? '#065f46' : pkg.status === 'cancelled' ? '#991b1b' : '#92400e',
                        whiteSpace: 'nowrap',
                      }}>
                        {pkg.status || 'pending'}
                      </span>
                    </div>

                    {/* Customer Return Label/QR Code */}
                    {pkg.label_url && (
                      <div style={{
                        marginBottom: '0.75rem',
                        padding: '1rem',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '2px solid #10b981',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#065f46', margin: 0 }}>
                            {pkg.package_type === 'qr' ? '📱 QR Code (Show to Scan)' : '🏷️ Return Shipping Label (Print Required)'}
                          </p>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <Button
                              size="sm"
                              onClick={() => {
                                const src = resolveLabelSrc(pkg);
                                if (pkg.package_type === 'qr') {
                                  openLabelInNewTab(src);
                                } else {
                                  printLabel(
                                    src,
                                    `${booking.confirmation_number}-pkg${idx + 1}-label`,
                                  );
                                }
                              }}
                              style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                              }}
                              data-testid={`booking-print-btn-${idx}`}
                            >
                              {pkg.package_type === 'qr' ? '📱 View Full Size' : '🖨️ Print Label'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                const src = resolveLabelSrc(pkg);
                                downloadLabel(
                                  src,
                                  `${booking.confirmation_number}-pkg${idx + 1}-label.png`,
                                );
                              }}
                              style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                              }}
                              data-testid={`booking-download-btn-${idx}`}
                            >
                              💾 Download
                            </Button>
                          </div>
                        </div>
                        <div
                          style={{
                            backgroundColor: 'white',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            display: 'inline-block',
                            cursor: 'pointer',
                          }}
                          onClick={() => openLabelInNewTab(resolveLabelSrc(pkg))}
                        >
                          <img
                            src={resolveLabelSrc(pkg)}
                            alt={pkg.package_type === 'qr' ? 'QR Code' : 'Return Label'}
                            style={{
                              maxWidth: pkg.package_type === 'qr' ? '150px' : '250px',
                              maxHeight: '200px',
                              display: 'block',
                              borderRadius: '4px',
                            }}
                          />
                        </div>
                        {pkg.package_type === 'label' && (
                          <p style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.5rem', marginBottom: 0, fontWeight: '600' }}>
                            ⚠️ Print this label and attach to package before pickup
                          </p>
                        )}
                      </div>
                    )}

                    {/* Status Update Buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <Button
                        size="sm"
                        onClick={() => updatePackageStatus(booking.id, idx, 'picked_up')}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#000000',
                          color: 'white',
                        }}
                      >
                        <Truck size={14} style={{ marginRight: '0.5rem' }} />
                        Picked Up
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updatePackageStatus(booking.id, idx, 'in_transit')}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                        }}
                      >
                        <Clock size={14} style={{ marginRight: '0.5rem' }} />
                        In Transit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updatePackageStatus(booking.id, idx, 'delivered')}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#10b981',
                          color: 'white',
                        }}
                      >
                        <CheckCircle size={14} style={{ marginRight: '0.5rem' }} />
                        Delivered
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setStatusUpdate({ bookingId: booking.id, packageId: idx, status: 'cancelled' });
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#b91c1c',
                          color: 'white',
                        }}
                      >
                        <AlertCircle size={14} style={{ marginRight: '0.5rem' }} />
                        Cancel
                      </Button>
                    </div>

                    {/* Cancellation Reason Dropdown */}
                    {statusUpdate.bookingId === booking.id && statusUpdate.packageId === idx && statusUpdate.status === 'cancelled' && (
                      <div style={{ padding: '1rem', backgroundColor: '#fef2f2', borderRadius: '8px', marginTop: '0.5rem', border: '2px solid #fca5a5' }}>
                        <h5 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.75rem', color: '#991b1b' }}>
                          ⚠️ Select Cancellation Reason:
                        </h5>
                        <select
                          value={cancellationReason}
                          onChange={(e) => setCancellationReason(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '2px solid #ef4444',
                            borderRadius: '8px',
                            marginBottom: '0.5rem',
                            fontSize: '1rem',
                            backgroundColor: 'white',
                            color: '#111827',
                            fontWeight: '500',
                          }}
                        >
                          <option value="">-- Select reason --</option>
                          {CANCELLATION_REASONS.map(reason => (
                            <option key={reason} value={reason}>
                              {reason.charAt(0).toUpperCase() + reason.slice(1)}
                            </option>
                          ))}
                        </select>

                        {cancellationReason === 'other' && (
                          <input
                            type="text"
                            placeholder="Enter custom reason..."
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              marginBottom: '0.5rem',
                              fontSize: '0.875rem',
                            }}
                          />
                        )}

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            size="sm"
                            onClick={() => updatePackageStatus(booking.id, idx, 'cancelled')}
                            disabled={!cancellationReason || (cancellationReason === 'other' && !customReason)}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              backgroundColor: '#dc2626',
                              color: 'white',
                            }}
                          >
                            <CheckCircle size={14} style={{ marginRight: '0.5rem' }} />
                            Confirm Cancellation
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setStatusUpdate({ bookingId: null, packageId: null, status: '' });
                              setCancellationReason('');
                              setCustomReason('');
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              backgroundColor: '#6b7280',
                              color: 'white',
                            }}
                          >
                            <X size={14} style={{ marginRight: '0.5rem' }} />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminBookingsTab;
